import { UserRequestProto, UserProto } from "build-ts/interface";
import showToast from "hooks/useCustomToast";
import {
  getCreateUserUrl,
  getListUsersUrl,
  getPasswordForgotUrl,
  getPasswordResetUrl,
  getPasswordVerifyOtpUrl,
  getUpdateUserUrl,
  getUserUrl,
} from "./newtonProperties";

export const deleteUser = async (userId: string): Promise<void> => {
  const response = await fetch(`${getUserUrl()}/${userId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to delete user.");
  }
};

export const listUsers = async (): Promise<UserProto[]> => {
  const response = await fetch(getListUsersUrl(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch users.");
  }
  const data = await response.json();
  return (Array.isArray(data) ? data : []).map((u: UserProto) => UserProto.fromJSON(u));
};

export const createUser = async (request: UserRequestProto): Promise<UserProto> => {
  const response = await fetch(getCreateUserUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(UserRequestProto.toJSON(request)),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.errorMsg ?? "Failed to create user.");
  }
  const data = await response.json();
  return UserProto.fromJSON(data);
};

export const updateUser = async (user: UserProto): Promise<UserProto> => {
  const response = await fetch(getUpdateUserUrl(), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(UserProto.toJSON(user)),
  });
  if (!response.ok) {
    throw new Error("Failed to update user.");
  }
  const data = await response.json();
  return UserProto.fromJSON(data);
};

/**
 * @param email - The user's email address.
 * @returns true if the OTP was sent successfully, false otherwise.
 */
export const sendForgotPasswordOtp = async (email: string): Promise<boolean> => {
  try {
    const url = new URL(getPasswordForgotUrl());
    url.searchParams.set("email", email);
    const response = await fetch(url.toString());
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      showToast("Error", data.errorMsg ?? "Please try again.", "error");
      return false;
    }
    return true;
  } catch (error) {
    showToast("Error sending code", "Please check your connection and try again.", "error");
    return false;
  }
};

/**
 * @param email - The user's email address.
 * @param otp   - The 6-digit OTP sent to the user's email.
 * @returns the reset token on success, or null on failure.
 */
export const verifyPasswordOtp = async (email: string, otp: string): Promise<string | null> => {
  try {
    const url = new URL(getPasswordVerifyOtpUrl());
    url.searchParams.set("email", email);
    url.searchParams.set("otp", otp);
    const response = await fetch(url.toString());
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      showToast("Invalid or expired code", data.errorMsg ?? "Please check the code and try again.", "error");
      return null;
    }
    const data = await response.json();
    return data.reset_token ?? null;
  } catch (error) {
    showToast("Error verifying code", "Please check your connection and try again.", "error");
    return null;
  }
};

/**
 * @param resetToken  - The short-lived reset token issued after OTP verification.
 * @param newPassword - The new plain-text password to set.
 * @returns true if the password was reset successfully, false otherwise.
 */
export const resetPassword = async (resetToken: string, newPassword: string): Promise<boolean> => {
  try {
    const url = new URL(getPasswordResetUrl());
    url.searchParams.set("resetToken", resetToken);
    url.searchParams.set("newPassword", newPassword);
    const response = await fetch(url.toString());
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      showToast(
        "Error resetting password",
        data.errorMsg ?? "Your reset link may have expired. Please try again.",
        "error"
      );
      return false;
    }
    showToast("Password updated", "You can now sign in with your new password.", "success");
    return true;
  } catch (error) {
    showToast("Error resetting password", "Please check your connection and try again.", "error");
    return false;
  }
};
